/**
 * Copies the given text to the clipboard.
 * Works in both secure (HTTPS/localhost) and insecure contexts.
 *
 * @param text The text content to copy.
 * @returns Promise<boolean> indicating whether the text was successfully copied.
 */
export async function copyToClipboard(text: string): Promise<boolean> {
  // Try modern API first
  if (navigator.clipboard && window.isSecureContext) {
    try {
      // Add a 1.5s timeout in case the Promise hangs (known issue in some environments)
      await Promise.race([
        navigator.clipboard.writeText(text),
        new Promise((_resolve, reject) =>
          setTimeout(() => reject(new Error('Clipboard timeout')), 1500)
        )
      ])
      return true
    } catch (err) {
      console.warn(
        'navigator.clipboard.writeText failed, trying fallback:',
        err
      )
    }
  }

  // Legacy fallback for insecure contexts or older browsers
  try {
    const textArea = document.createElement('textarea')
    textArea.value = text

    // Prevent scrolling to bottom of page
    textArea.style.position = 'fixed'
    textArea.style.top = '0'
    textArea.style.left = '0'
    textArea.style.width = '2em'
    textArea.style.height = '2em'
    textArea.style.padding = '0'
    textArea.style.border = 'none'
    textArea.style.outline = 'none'
    textArea.style.boxShadow = 'none'
    textArea.style.background = 'transparent'

    document.body.appendChild(textArea)

    textArea.focus()
    textArea.select()

    // For iOS
    textArea.setSelectionRange(0, 999999)

    const successful = document.execCommand('copy')
    document.body.removeChild(textArea)

    return successful
  } catch (err) {
    console.error('Fallback clipboard copy failed:', err)
    return false
  }
}
